import { AppModelsProgrammingSessionCancellationPolicyType as CancellationPolicyType } from "@classes-service-types/app-models-programming-session-cancellation-policy-type.ts";

export const DEFAULT_WITHDRAWAL_POLICY_URL = "https://apiabroad.com/withdrawal-policy-safeguards/";

type PolicySource = {
  cancellation_policy_type?: CancellationPolicyType | null;
  cancellation_policy_url?: string | null;
};

type ResolveWithdrawalPolicyUrlArgs = {
  enabled?: boolean;
  orderIsInDraft?: boolean;
  session?: PolicySource;
  order?: PolicySource;
};

// The policy in force: the session's own while the order is still a draft, the one
// snapshotted onto the order once it has been submitted.
const policyInForce = ({ orderIsInDraft, session, order }: ResolveWithdrawalPolicyUrlArgs) =>
  orderIsInDraft ? session : order;

// A policy type of None means the session deliberately carries no policy.
const policyApplies = (source: ResolveWithdrawalPolicyUrlArgs): boolean =>
  policyInForce(source)?.cancellation_policy_type !== CancellationPolicyType.None;

/**
 * Whether a Withdrawal Policy applies at all. A policy type of None means the session
 * deliberately carries no policy, so there is nothing to acknowledge or link to.
 */
export const hasWithdrawalPolicy = ({ enabled, ...source }: ResolveWithdrawalPolicyUrlArgs): boolean =>
  !enabled || policyApplies(source);

/**
 * The Withdrawal Policy link URL for checkout: the session's configured policy while the
 * order is a draft, the policy snapshotted on the order once submitted, and the static
 * policy when the flag is off or no configured URL is available.
 *
 * Returns undefined when no policy applies, which is distinct from a missing URL — that
 * still falls back to the static policy.
 */
export const resolveWithdrawalPolicyUrl = ({
  enabled,
  ...source
}: ResolveWithdrawalPolicyUrlArgs): string | undefined => {
  if (!enabled) {
    return DEFAULT_WITHDRAWAL_POLICY_URL;
  }
  if (!policyApplies(source)) {
    return undefined;
  }
  return policyInForce(source)?.cancellation_policy_url ?? DEFAULT_WITHDRAWAL_POLICY_URL;
};
